import { FC } from "react";
import { ReportHeader } from "../components/ReportHeader";
import moment from "moment";
import { formatToCurrency } from "utils/currency";
import { EvictionReport, VerificationReport } from "types/landlord/reports";
import api from "api/landlord/applications";
import { Applicant } from "types/application";
import { ReportAlertMessage } from "./CriminalReportSlice/components/ReportAlertMessage";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "shared/components/Loader";
import { Section } from "../components/Section";
import { Stat } from "../components/Stat";
import { ReportException } from "../components/ReportException";
import { VerificationNames, VerificationStatus } from "shared/types";
import { ReportCompletedBanner } from "../components/ReportCompletedBanner";
import { FinishedWithWarningsBanner } from "../components/FinishedWithWarningsBanner";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";
import { PrintReady } from "../providers/PrintProvider";
import { ApplicantSettings } from "shared/types";

const KeyPairValue: FC<{ label: string; value: string | number }> = ({
  label,
  value
}) => {
  return (
    <div className="max-w-1/2 flex w-full gap-2 px-8 py-2">
      <label className="min-w-[150px] text-sm font-medium text-content-300">
        {label}
      </label>
      <span className="max-w-[250px] grow text-sm font-medium capitalize text-content-1000">
        {value || "N/A"}
      </span>
    </div>
  );
};

type EvictionReportSliceProps = {
  applicant: Applicant;
  verificationId: number;
  screeningCriteria?: ApplicantSettings;
};

export const EvictionReportSlice: FC<EvictionReportSliceProps> = ({
  applicant,
  verificationId,
  screeningCriteria
}) => {
  const { data: verification, isLoading } = useQuery<
    VerificationReport<EvictionReport>
  >({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.EvictionReport,
      applicationId: applicant.application_id,
      applicantId: applicant.id,
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id,
          applicant.id,
          verificationId
        )
        .then((res) => res.verification)
  });

  const report = verification?.report;
  const { results = [], criteria } = report ?? {};
  const eventsReported = results.length;

  const housingCourtRecordsValue =
    applicant.criteria_check_settings?.housing_court_records.value;
  const housingCourtRecordsYears =
    applicant.template_criteria_settings?.housing_court_records
      ?.housing_court_records_years;

  if (isLoading)
    return (
      <>
        <ReportHeader
          title="Housing Court Report"
          name={VerificationNames.EvictionReport}
          reportDate=""
          verificationId={0}
        />
        <Loader />
      </>
    );

  if (
    Object.keys(report || {}).length === 0 ||
    verification?.status === VerificationStatus.Failed
  )
    return (
      <ReportException
        verificationId={verificationId}
        title="Housing court report"
      />
    );

  return (
    <div className="flex-1">
      <ReportHeader
        title="Housing court report"
        reportDate={verification?.updated_at}
        verificationId={verificationId}
      />
      <PrintReady verificationId={verificationId} />

      <ReportAlertMessage title="Disclaimer" message={report?.disclaimer} />
      <ReportAlertMessage
        title="Consumer statement"
        message={report?.consumer_statement}
      />

      <Section sectionTitle="Housing court records summary" maxWidth="none">
        {verification?.completed_at ? (
          <ReportCompletedBanner
            completedAt={verification.completed_at}
            verificationName={VerificationNames.EvictionReport}
            reportId={String(verificationId)}
            vendor={verification.vendor}
          />
        ) : null}

        {verification?.status === VerificationStatus.FinishedWithWarnings &&
        verification.report.notice ? (
          <FinishedWithWarningsBanner msg={verification.report.notice} />
        ) : null}

        <Stat
          title="Events reported"
          value={String(eventsReported)}
          width="240px"
          hasCriteria={!!criteria}
          criteriaValue={criteria?.housing_court_records}
          additionalCriteriaValue={String(housingCourtRecordsValue)}
          criteriaYears={housingCourtRecordsYears}
          criteriaSuccess={
            (criteria?.housing_court_records || 0) <=
            (criteria?.housing_court_records || 0)
          }
          criteriaImportance={
            screeningCriteria?.housing_court_records?.importance
          }
        />
      </Section>

      {results.map((result, index) => (
        <div className="pt-4" key={`report-${verificationId}-${index}`}>
          <div
            className="mb-2 flex items-center justify-between rounded-lg p-4"
            style={{
              backgroundColor: "#FAF8F9",
              border: "1px solid rgba(28, 29, 34, 0.04)"
            }}
          >
            <div className="flex flex-1 items-center gap-1">
              <img
                src="/assets/housing_court_report_logo.svg"
                alt="housing court logo"
              />
              <p className="ml-1 text-sm font-medium capitalize text-content-1000">
                {`${result.address}, ${result.city}, ${result.state} ${result.zip}`}{" "}
              </p>
            </div>

            <div className="flex w-1/2 flex-row justify-end">
              <label className="mr-4 text-sm font-medium text-content-300">
                Filling Date:
              </label>
              <span className="text-sm font-medium text-content-1000">
                {moment(result.filling_date).format("MMM DD, YYYY")}
              </span>
            </div>
          </div>

          <div className="flew-row flex">
            <KeyPairValue
              label="Judgement amount"
              value={
                result.judgement_amount
                  ? formatToCurrency(result.judgement_amount)
                  : ""
              }
            />
            <KeyPairValue label="Plaintiff" value={result.plaintiff} />
          </div>

          <div className="flex">
            <KeyPairValue label="Court" value={result.court} />
            <KeyPairValue label="Notice Type" value={result.notice_type} />
          </div>

          <div className="flex">
            <KeyPairValue label="Case Number" value={result.case_number} />
          </div>
        </div>
      ))}
    </div>
  );
};
